import React, { useEffect, useRef, useState } from "react";

const isTouch = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(pointer: coarse)").matches ||
    "ontouchstart" in window);

export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const posRef = useRef({ x: 0, y: 0, rx: 0, ry: 0 });
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default"); // default | hover | text

  useEffect(() => {
    if (isTouch()) return;
    setEnabled(true);

    const onMove = (e) => {
      posRef.current.x = e.clientX;
      posRef.current.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX - 4}px, ${e.clientY - 4}px, 0)`;
      }
    };
    const onDown = () => setVariant("click");
    const onUp = () => setVariant((v) => (v === "click" ? "default" : v));

    const onOver = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest("button, a, [role='button'], input, textarea, select, [data-cursor='hover']")) {
        setVariant("hover");
      } else if (t.closest("h1, h2, h3, p, [data-cursor='text']")) {
        setVariant("text");
      } else {
        setVariant("default");
      }
    };

    let raf;
    const tick = () => {
      const p = posRef.current;
      // ease ring toward dot
      p.rx += (p.x - p.rx) * 0.18;
      p.ry += (p.y - p.ry) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${p.rx - 18}px, ${p.ry - 18}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("mouseover", onOver, { passive: true });

    document.documentElement.classList.add("custom-cursor-active");

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("mouseover", onOver);
      document.documentElement.classList.remove("custom-cursor-active");
    };
  }, []);

  if (!enabled) return null;

  const ringSize =
    variant === "hover" ? 64 : variant === "text" ? 48 : variant === "click" ? 26 : 36;
  const ringBg =
    variant === "hover"
      ? "rgba(255, 122, 0, 0.14)"
      : variant === "text"
        ? "rgba(0, 200, 83, 0.10)"
        : "transparent";
  const ringBorder =
    variant === "hover"
      ? "1px solid rgba(255, 122, 0, 0.65)"
      : variant === "text"
        ? "1px solid rgba(0, 200, 83, 0.55)"
        : "1px solid rgba(255, 255, 255, 0.45)";

  return (
    <>
      <div
        ref={ringRef}
        aria-hidden
        className="cursor-ring"
        style={{
          width: ringSize,
          height: ringSize,
          background: ringBg,
          border: ringBorder,
          marginLeft: (36 - ringSize) / 2,
          marginTop: (36 - ringSize) / 2,
          backdropFilter: variant === "hover" ? "blur(2px)" : "none",
          mixBlendMode: variant === "hover" ? "normal" : "difference",
        }}
      />
      <div ref={dotRef} aria-hidden className="cursor-dot" />
    </>
  );
};

export default CustomCursor;
